import useChallengesQuery from "@hooks/common/challenges/use-challenges-query";
import useGlobalContext from "@hooks/common/general/use-global-context";
import { Box, Skeleton, Stack, SxProps, useTheme } from "@mui/material";
import { GetChallengesRequest } from "@Shared/schemas/challenges/challengeSchemas";
import { useEffect, useState } from "react";
import "swiper/css";
import { SwiperSlide } from "swiper/react";
import { HorizontalScroller } from "../common/horizontal-scroller";
import { BybStepper } from "../common/styled-components";
import ChallengeCardLarge from "./cards/challenge-card-large";
import { getFirstNonCompletedChallenge } from "./utils";

interface IChallengeRowLarge {
  tag?: string;
  padding: string;
  caption?: string;
  filters?: GetChallengesRequest["filters"];
  limit?: number;
  orderBy?: GetChallengesRequest["order_by"];
  sx?: SxProps;
}

/**
 *
 * @param tag - Determines which Challenges are shown in the row
 * @param padding - Needed to make it look like the Challenge row adheres to the padding of the parent container, while we really need the row to extend the whole width of the page in order to make scroling between cards look good
 * @param caption - Optional caption to be shown instead of League and Division
 * @param filters - Filter the Challenges
 * @param limit - Limit the number of Challenges shown
 * @param orderBy - Order the Challenges by a specific field. Default is "unlocked_at" in ascending order
 * @param sx - Custom styles
 *
 * @returns A horizontally scrollable row of Challenge cards that are intended to fill the whole width of the screen (only one card shown at a time)
 */
const ChallengeRowLarge = ({
  tag,
  padding,
  caption,
  limit,
  filters = {},
  orderBy = [
    {
      field: "unlocked_at",
      direction: "asc",
    },
  ],
  sx,
}: IChallengeRowLarge) => {
  const { globalState } = useGlobalContext();
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [swiper, setSwiper] = useState<any>();
  const [initialSlide, setInitialSlide] = useState<number>();

  const { challenges } = useChallengesQuery({
    exclude_tags: !globalState.user.isSuperAdmin ? ["QA", "DEVELOPMENT"] : [],
    unpublished_allowed: globalState.user.isSuperAdmin,
    filters,
    order_by: orderBy,
    ...(limit && { limit }),
    ...(tag && { include_tags: [tag] }),
  });

  useEffect(() => {
    swiper?.on("activeIndexChange", function ({ realIndex }: any) {
      setActiveStep(realIndex);
    });
  }, [swiper]);

  useEffect(() => {
    if (challenges) {
      const currentChallengeIndex = getFirstNonCompletedChallenge(challenges);
      setInitialSlide(currentChallengeIndex);
      setActiveStep(currentChallengeIndex);
    }
  }, [challenges]);

  const maxSteps = challenges?.length || 0;
  return challenges && challenges.length > 0 && initialSlide !== undefined ? (
    <Stack sx={{ position: "relative", ...sx }}>
      <HorizontalScroller
        onSwiper={setSwiper}
        padding={padding}
        initialSlide={initialSlide}
        slidesPerGroup={1}
        freeMode={{ enabled: false }}
        navigation={false}
      >
        {challenges.map(challenge => (
          <SwiperSlide key={challenge.id} style={{ cursor: "grab", height: "auto", width: `calc(100% - ${padding})`, margin: padding }}>
            <ChallengeCardLarge
              challenge={challenge}
              disabled={(challenge.unlocked_at || 0) > (globalState.user.userRating || 0)}
              caption={caption}
            />
          </SwiperSlide>
        ))}
      </HorizontalScroller>
      <Box>
        {maxSteps > 1 && (
          <BybStepper
            steps={maxSteps}
            position="static"
            activeStep={activeStep}
            nextButton={null}
            backButton={null}
            sx={{ marginBottom: theme.spacing(6) }}
          />
        )}
      </Box>
    </Stack>
  ) : (
    <Skeleton variant="rounded" height={512} width="100%" sx={{ borderRadius: theme.spacing(3) }} />
  );
};

export default ChallengeRowLarge;
